import { Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { PREDEFINED_BANK_TRANSACTION_FILTERS } from "../../status";
import type { BackendFilters } from "../../utils/backendFilters";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "../../utils/featureFlags";
import ReconciliationsTable from "../ReconciliationsTable";
import NewReconciliation from "./NewReconciliation";

interface ReconciliationListViewProps {
  transactions: BankTransactionMessage[];
  isLoading: boolean;
  transactionsLoading: boolean;
  selectedPractices: PracticeMessage[];
  uploadBankTransactions: (practiceId: string, file: File) => Promise<void>;
  updateTransaction: (
    transactionUpdate: BankTransactionUpdateMessage,
  ) => Promise<void>;
  onFindMatches?: (transaction: BankTransactionMessage) => void;
  onUnmatchTransaction?: (wieldyId: string) => Promise<void>;
  selectedFilter: PREDEFINED_BANK_TRANSACTION_FILTERS;
  setSelectedFilter: (filter: PREDEFINED_BANK_TRANSACTION_FILTERS) => void;
  onFilterChange?: (filters: BackendFilters) => void;
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
  page: number;
  pageSize: number;
  onPageChange?: (page: number, pageSize: number) => void;
  rowCount?: number;
  pendingIds?: Set<string>;
}

export default function ReconciliationListView({
  transactions,
  isLoading,
  transactionsLoading,
  selectedPractices,
  uploadBankTransactions,
  updateTransaction,
  onFindMatches,
  onUnmatchTransaction,
  selectedFilter,
  setSelectedFilter,
  onFilterChange,
  onSortChange,
  page,
  pageSize,
  onPageChange,
  rowCount,
  pendingIds,
}: ReconciliationListViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleTransactionUpdate = async (
    transactionUpdate: BankTransactionUpdateMessage,
  ) => {
    await updateTransaction(transactionUpdate);
  };

  const [open, setOpen] = useState(false);
  const { isEnabled: bankConnectionsEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.BANK_CONNECTIONS,
  );

  const filterFromQueryParam = (): PREDEFINED_BANK_TRANSACTION_FILTERS => {
    const statusParam = searchParams.get("status");
    if (!statusParam) {
      return PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW;
    }

    switch (statusParam.toLowerCase()) {
      case "review":
        return PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW;
      case "pending":
        return PREDEFINED_BANK_TRANSACTION_FILTERS.PENDING;
      case "all":
        return PREDEFINED_BANK_TRANSACTION_FILTERS.ALL;
      default:
        return PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding filterFromQueryParam or dropping searchParams would change when this runs
  useEffect(() => {
    setSelectedFilter(filterFromQueryParam());
  }, [searchParams, setSelectedFilter]);

  return (
    <>
      <h1>Bank Reconciliation</h1>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "14px",
        }}
      >
        <div>
          {!bankConnectionsEnabled && (
            <Button
              aria-label="Upload New Bank Statement"
              variant="contained"
              onClick={() => {
                setOpen(true);
              }}
              style={{ marginBottom: "14px" }}
            >
              Upload New Bank Statement
            </Button>
          )}

          <div>
            <ToggleButtonGroup
              size="medium"
              color="primary"
              value={selectedFilter}
              exclusive
              onChange={(_, newfilter) => {
                if (newfilter && selectedFilter !== newfilter) {
                  const params = new URLSearchParams(location.search);
                  params.set("status", newfilter.toLowerCase());
                  navigate({ search: params.toString() }, { replace: true });
                  setSelectedFilter(newfilter);
                }
              }}
              aria-label="Bank Transaction Filters"
            >
              {Object.values(PREDEFINED_BANK_TRANSACTION_FILTERS).map(
                (filter) => (
                  <ToggleButton
                    key={filter}
                    value={filter}
                    aria-label={`${filter} filter`}
                  >
                    {filter}
                  </ToggleButton>
                ),
              )}
            </ToggleButtonGroup>
          </div>
        </div>
      </div>

      {!bankConnectionsEnabled && (
        <NewReconciliation
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          selectedPractices={selectedPractices}
          uploadBankTransactions={uploadBankTransactions}
        />
      )}
      <ReconciliationsTable
        persistKey="reconciliationListView"
        isLoading={transactionsLoading || isLoading}
        transactions={transactions}
        updateTransaction={handleTransactionUpdate}
        onFindMatches={onFindMatches}
        onUnmatchTransaction={onUnmatchTransaction}
        selectedFilter={selectedFilter}
        onFilterChange={onFilterChange}
        onSortChange={onSortChange}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        rowCount={rowCount}
        pendingIds={pendingIds}
      />
    </>
  );
}
